import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, Tooltip, Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminAPI, Organization, ActivityTimeseries } from '../lib/api';
import { TrendingUp } from 'lucide-react';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const TYPE_LABELS: Record<string, string> = {
  user_login: 'Logins',
  agent_created: 'Agents created',
  agent_updated: 'Agents updated',
  component_created: 'Components created',
  test_agent_created: 'Test agents created',
  test_agent_run_started: 'Test runs',
  interaction_started: 'Interactions',
  knowledge_base_created: 'KBs created',
  kb_document_added: 'KB docs added',
};

const TYPE_COLORS: Record<string, string> = {
  user_login: '#3b82f6',
  agent_created: '#8b5cf6',
  agent_updated: '#a78bfa',
  component_created: '#ec4899',
  test_agent_created: '#f59e0b',
  test_agent_run_started: '#fbbf24',
  interaction_started: '#10b981',
  knowledge_base_created: '#14b8a6',
  kb_document_added: '#06b6d4',
};

/** "kb_document_added" → "Kb document added" for types the label map doesn't know. */
const labelFor = (type: string): string => {
  if (TYPE_LABELS[type]) return TYPE_LABELS[type];
  const words = type.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const ActivityPage: React.FC = () => {
  const { token } = useAdminAuth();
  const api = useMemo(() => new AdminAPI(token!), [token]);

  const today = new Date();
  const monthAgo = new Date(); monthAgo.setDate(monthAgo.getDate() - 29);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  const [orgId, setOrgId] = useState('');
  const [from, setFrom] = useState(fmt(monthAgo));
  const [to, setTo] = useState(fmt(today));
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [data, setData] = useState<ActivityTimeseries | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = { organization_id: orgId || undefined, from, to };
      const [orgList, d] = await Promise.all([
        orgs.length ? Promise.resolve(orgs) : api.getOrganizations(),
        api.getActivityTimeseries(params),
      ]);
      if (!orgs.length) setOrgs(orgList as Organization[]);
      setData(d);
    } catch (e) {
      const why = e instanceof Error ? e.message : '';
      setErr(why ? `Couldn't load activity: ${why}` : "Couldn't load activity. Retry, or check the API.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [orgId, from, to]);

  const chart = useMemo(() => {
    if (!data) return { labels: [], datasets: [] };
    const days = Array.from(new Set(data.points.map(p => p.day))).sort();
    const types = Array.from(new Set(data.points.map(p => p.type)));
    return {
      labels: days,
      datasets: types.map(t => ({
        label: labelFor(t),
        backgroundColor: TYPE_COLORS[t] || '#94a3b8',
        data: days.map(d => data.points.find(p => p.day === d && p.type === t)?.count || 0),
      })),
    };
  }, [data]);

  return (
    <div className="max-w-7xl space-y-6">
      <div className="flex items-center space-x-3">
        <TrendingUp className="w-7 h-7 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Activity</h1>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center justify-between gap-3">
          <span>{err}</span>
          <button
            onClick={refresh}
            disabled={loading}
            className="shrink-0 px-3 py-1 rounded border border-red-300 bg-white text-sm text-red-700 hover:bg-red-100 disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-4 bg-white p-4 rounded-lg shadow">
        <div>
          <label className="block text-xs text-gray-600">Organization</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)} className="rounded border-gray-300 text-sm">
            <option value="">All organizations</option>
            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded border-gray-300 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-600">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded border-gray-300 text-sm" />
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {loading && <div className="text-sm text-gray-500">Loading…</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {data?.tiles.map(t => (
          <div key={t.type} className="bg-white p-4 rounded-lg shadow">
            <div className="text-xs uppercase tracking-wider text-gray-500">{labelFor(t.type)}</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{t.count.toLocaleString()}</div>
            <div className="mt-1 text-xs text-gray-500">{t.unique_users.toLocaleString()} unique users</div>
          </div>
        ))}
        {data && data.tiles.length === 0 && (
          <div className="col-span-full bg-white p-6 rounded-lg shadow text-sm text-gray-500 text-center">
            No activity yet for this window.
          </div>
        )}
      </div>

      <div className="bg-white p-4 rounded-lg shadow">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Events per day, stacked by type</h4>
        {!data ? (
          <p className="py-8 text-sm text-gray-500 text-center">
            {loading ? 'Loading…' : 'Nothing to chart until the activity loads.'}
          </p>
        ) : chart.labels.length === 0 ? (
          <p className="py-8 text-sm text-gray-500 text-center">No activity in this window.</p>
        ) : (
          <Bar data={chart} options={{
            responsive: true,
            scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } },
            plugins: { legend: { position: 'bottom' } },
          }} />
        )}
      </div>
    </div>
  );
};
